import { useSyncExternalStore } from 'react';
import i18n from './i18n';

// One language at a time, on the site and in the slime world: English or Japanese, never both stacked.
// It starts in English only on a device set to English, otherwise Japanese, and the choice is kept on this device
// (i18n.ts). The site's language button and the ⚙ card in the slime world both change it.
// Learning content (questions, answers, sentences to fix, Meteor phrases, slime names) is English in both.

export type Lang = 'en' | 'ja';

export function lang(): Lang {
  return i18n.language?.startsWith('en') ? 'en' : 'ja';
}

export function setLang(next: Lang) {
  void i18n.changeLanguage(next);
}

export function toggleLang() {
  setLang(lang() === 'en' ? 'ja' : 'en');
}

// for code outside React (the chalkboard's canvas): call back when the language changes
export function onLang(cb: (l: Lang) => void): () => void {
  const h = () => cb(lang());
  i18n.on('languageChanged', h);
  return () => i18n.off('languageChanged', h);
}

function subscribe(cb: () => void) {
  return onLang(cb);
}

// a component that shows words calls this, so it redraws when the language changes
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, lang, lang);
}

// Strings are written "English · 日本語" so both halves sit together in the code and stay easy to write;
// this keeps the half for the language in use. A string with no Japanese half is the same in both.
const SPLIT = / · (?=[0-9A-Za-z+]*[　-ヿ一-鿿！-～])/;
export function tr(text: string, l: Lang = lang()): string {
  const at = text.search(SPLIT);
  if (at < 0) return text;
  return l === 'en' ? text.slice(0, at) : text.slice(at + 3);
}

// the same for words built at run time: pick one of the two
export function L<T>(en: T, ja: T, l: Lang = lang()): T {
  return l === 'en' ? en : ja;
}

// the page says which language it is in, so screen readers read it right and Japanese wraps at phrase boundaries
function mark() {
  document.documentElement.lang = lang();
}
if (typeof document !== 'undefined') {
  mark();
  onLang(mark);
}
